import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import ClientWizard from "@/components/ClientWizard";
import { useClient } from "@/context/ClientContext";
import { supabase } from "@/lib/supabaseClient";
import type { Tables } from "@/lib/supabaseClient";
import { createClient } from "@/services/clientOnboardingService";
import { getRecentClientIds } from "@/services/recentClients";

type Client = Tables<"clients">;

export default function ClientsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { reload: reloadClientContext } = useClient();
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  // Open wizard when URL has ?new=true parameter
  useEffect(() => {
    if (searchParams.get("new") === "true") {
      setIsWizardOpen(true);
    }
  }, [searchParams]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  // Load clients from Supabase
  useEffect(() => {
    async function fetchClients() {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from("clients")
          .select("*")
          .is("deleted_at", null)
          .order("created_at", { ascending: false });

        if (error) throw error;
        setClients(data || []);
      } catch (err) {
        console.error("Error fetching clients:", err);
        setError(err instanceof Error ? err.message : "Failed to load clients");
      } finally {
        setIsLoading(false);
      }
    }

    void fetchClients();
  }, []);

  const handleCloseWizard = () => {
    setIsWizardOpen(false);
    if (searchParams.get("new")) {
      setSearchParams({});
    }
  };

  const handleCreateClient = async (clientData: {
    name: string;
    shortName?: string;
    notes?: string;
  }) => {
    try {
      console.log("Creating client with onboarding:", clientData);

      // Use the new onboarding service that auto-provisions KB
      // Note: Contact info can be added later via client settings
      const newClient = await createClient({
        name: clientData.name,
        shortName: clientData.shortName,
        notes: clientData.notes,
      });

      // Fetch the full client record to add to local list
      const { data: fullClient } = await supabase
        .from("clients")
        .select("*")
        .eq("id", newClient.id)
        .single();

      if (fullClient) {
        setClients((prev) => [fullClient, ...prev]);
      }

      // Reload the ClientContext to update global client list
      await reloadClientContext();

      // Close wizard and clear URL params
      handleCloseWizard();

      // Navigate to the new client's page
      // Note: ClientContext will auto-update when navigating
      void navigate(`/w/${newClient.slug || newClient.id}`);

      console.log("✓ Client created and knowledge base provisioned");
    } catch (err) {
      console.error("Error creating client:", err);
      setError(err instanceof Error ? err.message : "Failed to create client");
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <header className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Clients
          </h1>
        </header>
        <div className="text-center py-12">
          <p className="text-slate-600">Loading clients...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <header className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Clients
          </h1>
        </header>
        <div className="text-center py-12">
          <p className="text-red-600">Error: {error}</p>
          <button
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show empty state when no clients
  if (clients.length === 0) {
    return (
      <>
        <div className="p-6">
          <div className="flex justify-between mb-4">
            <h1 className="text-xl font-semibold">Clients</h1>
            <button
              className="px-3 py-1.5 border rounded-md bg-white"
              onClick={() => setIsWizardOpen(true)}
            >
              New Client
            </button>
          </div>

          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                No clients yet
              </h2>
              <p className="text-gray-600 mb-6">
                Create your first client to start managing campaigns and
                tracking performance.
              </p>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                onClick={() => setIsWizardOpen(true)}
              >
                Create Your First Client
              </button>
            </div>
          </div>
        </div>

        <ClientWizard
          open={isWizardOpen}
          onClose={handleCloseWizard}
          onComplete={handleCreateClient}
        />
      </>
    );
  }

  const q = query.trim().toLowerCase();
  const visibleClients = q
    ? clients.filter((c) => c.name.toLowerCase().includes(q))
    : clients;

  const byId = new Map(clients.map((c) => [c.id, c]));
  const recentClients = getRecentClientIds()
    .map((id) => byId.get(id))
    .filter((c): c is (typeof clients)[number] => Boolean(c))
    .slice(0, 5);

  return (
    <>
      <div className="space-y-6">
        <header className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Clients
          </h1>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
            onClick={() => setIsWizardOpen(true)}
          >
            New Client
          </button>
        </header>

        <div>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search clients"
            aria-label="Search clients"
            className="w-full max-w-sm rounded-md border border-slate-200 px-3 py-2 text-sm"
          />
        </div>

        {recentClients.length > 0 && (
          <section aria-labelledby="recently-opened-heading">
            <h2
              id="recently-opened-heading"
              className="text-sm font-semibold text-[var(--text-secondary)] mb-2"
            >
              Recently opened
            </h2>
            <div className="flex flex-wrap gap-2">
              {recentClients.map((client) => (
                <button
                  key={client.id}
                  onClick={() => {
                    void navigate(`/w/${client.slug || client.id}`);
                  }}
                  className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
                >
                  {client.name}
                </button>
              ))}
            </div>
          </section>
        )}

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          <table className="w-full text-left">
            <thead className="text-sm text-slate-600 bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="px-6 py-4 font-semibold">Name</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {visibleClients.map((client) => (
                <tr
                  key={client.id}
                  className={`hover:bg-slate-50 ${
                    client.is_demo
                      ? "bg-indigo-50/50 border-l-4 border-l-indigo-500"
                      : "bg-white"
                  }`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-800 font-medium">
                        {client.name}
                      </span>
                      {client.is_demo && (
                        <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 ring-1 ring-inset ring-indigo-200">
                          Demo
                        </span>
                      )}
                    </div>
                    {client.is_demo && (
                      <p className="text-xs text-[var(--text-muted)] mt-0.5">
                        Explore Nexus with sample data
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                        client.is_active
                          ? "bg-emerald-100 text-emerald-800 ring-emerald-200"
                          : "bg-slate-100 text-slate-800 ring-slate-200"
                      }`}
                    >
                      {client.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      className="text-blue-600 hover:text-blue-800 font-medium"
                      onClick={() => {
                        void navigate(`/w/${client.slug || client.id}`);
                      }}
                    >
                      {client.is_demo ? "Explore" : "View"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ClientWizard
        open={isWizardOpen}
        onClose={handleCloseWizard}
        onComplete={handleCreateClient}
      />
    </>
  );
}
